import { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { inviteInputClass } from "./InviteField";
import { useAddressSuggestions } from "./useAddressSuggestions";

export const InviteAddressAutocomplete = ({ form, update, setForm, disabled, onResolvingChange }) => {
  const list = useRef(null);
  const { suggestions, status, activeIndex, setActiveIndex, search, select, clear } = useAddressSuggestions({
    enabled: form.country === "US" && !disabled,
    onSelect: address => setForm(current => ({ ...current, ...address, address2: address.address2 || current.address2 })),
  });
  const open = suggestions.length > 0;
  useEffect(() => { onResolvingChange(status === "selecting"); }, [status, onResolvingChange]);
  useEffect(() => {
    if (activeIndex >= 0) list.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);
  const keyDown = event => {
    if (event.key === "Escape") { event.preventDefault(); clear(); }
    if (open && ["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      setActiveIndex(current => event.key === "ArrowDown" ? (current + 1) % suggestions.length : (current < 0 ? suggestions.length - 1 : (current - 1 + suggestions.length) % suggestions.length));
    }
    if (event.key === "Enter" && (open || status === "loading" || status === "selecting")) {
      event.preventDefault();
      if (open) select(activeIndex < 0 ? 0 : activeIndex);
    }
  };
  const message = {
    loading: "Finding addresses…", selecting: "Filling your address…", selected: "Address filled. You can edit any detail.",
    empty: "No matching addresses. You can enter yours manually.", error: "Address suggestions are unavailable. Please enter your address manually.",
  }[status];
  return (
    <div className="relative min-w-0 sm:col-span-2" data-testid="invite-address-autocomplete">
      <div className="relative">
      <label htmlFor="invite-address1" className="block text-xs text-[color:var(--ink-3)]" data-testid="invite-address1-label">Street address</label>
      <Input id="invite-address1" name="address1" required maxLength={200} disabled={disabled}
        autoComplete="address-line1" role="combobox" aria-autocomplete="list" aria-expanded={open}
        aria-controls={open ? "invite-address-suggestions" : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? `invite-address-option-${activeIndex}` : undefined}
        aria-describedby="invite-address-status" className={inviteInputClass} value={form.address1}
        placeholder={form.country === "US" ? "Start typing your street address" : undefined}
        onChange={event => { update(event); search(event.target.value); }} onFocus={() => search(form.address1)}
        onBlur={clear} onKeyDown={keyDown} data-testid="invite-address1-input" />
      {open && <div className="absolute inset-x-0 top-full z-30 border border-[#0f0f10]/20 bg-[#faf9f5] shadow-lg" data-testid="invite-address-suggestions-panel">
        <ul ref={list} id="invite-address-suggestions" role="listbox" aria-label="US street addresses" className="max-h-60 overflow-y-auto" data-testid="invite-address-suggestions">
          {suggestions.map((item, index) => <li key={item.placePrediction.placeId || index} role="option" aria-selected={index === activeIndex}
            id={`invite-address-option-${index}`} data-testid={`invite-address-option-${index}`}
            onPointerDown={event => event.preventDefault()} onClick={() => select(index)} onMouseEnter={() => setActiveIndex(index)}
            className={`cursor-pointer border-b border-[#0f0f10]/10 px-4 py-3 text-sm leading-relaxed transition-colors ${index === activeIndex ? "bg-[#a8c69f]/25" : "hover:bg-[#a8c69f]/15"}`}>
            {item.placePrediction.text.toString()}
          </li>)}
        </ul>
        <div className="px-3 pb-2 pt-3 text-right text-xs text-[#555]" data-testid="invite-google-attribution">Google Maps</div>
      </div>}
      </div>
      {/* Reserve status space: clearing feedback on blur must not move the next click target. */}
      <p id="invite-address-status" role="status" aria-live="polite" className="mt-2 min-h-[2.5rem] text-[11px] leading-relaxed text-[color:var(--ink-3)]" data-testid="invite-address-status">{message || ""}</p>
    </div>
  );
};